import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { COLORS } from "../../constants/Theme";
import { generateDisabledStyling, generateInverseHoverBackground, getBackgroundColor, getBorder, getClickEffect, getColor, getDashedBorderStyling, getDisabledTextColor, getHoverButtonBackground, getHoverButtonBackgroundCustom, getHoverButtonTextDecorationStyle, getLoadingBackground, getLoadingBorder, getLoadingButtonBackgroundCustom, getPadding, } from './themes';
export const Button = styled.button `
	all: unset;
	cursor: pointer;
	display: flex;
	gap: 8.83px;
	border-radius: ${(props) => (props.buttonType == 'text' ? '0' : '4px')};
	overflow: hidden;
	${(props) => (props.matchParentWidth ? `flex-grow: 1;` : '')}
	background-color: ${(props) => getBackgroundColor(props.buttonType, props.size, props.inverse, props.darkMode, props.buttonColor)};

	${(props) => {
    var _a;
    return props.buttonType.startsWith('dash')
        ? `.button-container {
					display: flex;
					width: 100%;
					position: relative;
					border-radius: 4px;
					overflow: hidden;
					${props.matchParentWidth ? 'justify-content: center;' : ''}
					padding: ${getPadding(props.buttonType, props.size)};
				}`
        : `padding: ${getPadding(props.buttonType, props.size)};
	${getBorder((_a = props.version) !== null && _a !== void 0 ? _a : '1.0', props.buttonType, props.size, props.inverse, props.error)}`;
}}

	.icon-component {
		height: ${(props) => (props.size === 'chip' ? '16px' : '20px')};
		width: ${(props) => (props.size === 'chip' ? '16px' : '20px')};
	}

	.icon-trailing {
		height: ${(props) => (props.size === 'chip' ? '16px' : '20px')};
		width: ${(props) => (props.size === 'chip' ? '16px' : '20px')};
	}

	.text {
		color: ${(props) => getColor(props.buttonType, props.size, props.inverse, props.darkMode)};
	}

	.icon-leading {
		height: ${(props) => (props.size === 'chip' ? '16px' : '20px')};
		width: ${(props) => (props.size === 'chip' ? '16px' : '20px')};
	}

	${(props) => getDashedBorderStyling(props.buttonType, props.size, props.isLoading
    ? COLORS.stroke.brandLightAlt
    : props.error
        ? COLORS.stroke.negative.vibrant
        : undefined)}

	.overlay-container {
		display: none;
	}

	${(props) => !props.disabled &&
    !props.isLoading &&
    props.activated &&
    (props.buttonType == 'dashBold' || props.buttonType == 'dashRegular') &&
    `
	.button-container {
		display: flex;
		${props.matchParentWidth ? 'justify-content: center;' : ''}
		position: relative;
		width: 100%;
		.overlay-container {
			display: none !important;
		}
		${`.text {
			color: ${COLORS.content.brand} !important;
		}
		.icon {
			color: ${COLORS.background.brand};
		}
		`}
	}
	${getClickEffect(props.buttonType, props.size, props.inverse)}
	${getDashedBorderStyling(props.buttonType, props.size, COLORS.content.brand)}
	`}

	:disabled {
		.overlay-container {
			display: none !important;
		}
		cursor: ${(props) => props.buttonType === 'text' ? 'auto' : 'not-allowed'};
		${(props) => generateDisabledStyling(props.buttonType, props.size, props.inverse)}
		.text {
			color: ${(props) => getDisabledTextColor(props.inverse)} !important;
		}
		${(props) => getDashedBorderStyling(props.buttonType, props.size, COLORS.stroke.primary)}
	}

	${(props) => !props.disabled &&
    !props.isLoading &&
    `:active {
				.button-container {
					display: flex;
					${props.matchParentWidth ? 'justify-content: center;' : ''}
					position: relative;
					width: 100%;
					.overlay-container {
						display: none !important;
					}
					${props.size === 'chip' &&
        `.text {
						color: ${COLORS.background.inverseLight} !important;
					}
					.icon {
						color: ${COLORS.background.inverseLight};
					}
					`}
				}


			${getClickEffect(props.buttonType, props.size, props.inverse)}
			${getDashedBorderStyling(props.buttonType, props.size, COLORS.content.brand)}
		}`}

	${(props) => 
// do not load hover styles for loading state and also active state
!props.isLoading
    ? `:not(:active):not(:disabled):hover {
			padding: 0;
			.button-container {
				display: flex;
				position: relative;
				width: 100%;
				${props.matchParentWidth ? 'justify-content: center;' : ''}
				padding: ${getPadding(props.buttonType, props.size)};
				{${props.buttonColor
        ? `background-color: ${getHoverButtonBackgroundCustom(props.buttonColor, 15)}`
        : ''}}
				.overlay-container {
					display: block;
					position: absolute;
					padding: 0;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					${getHoverButtonBackground(props.buttonType, props.inverse)}
				}
				${props.size === 'chip' && props.buttonType !== 'text'
        ? props.darkMode
            ? `background-color: ${COLORS.stroke.primary};`
            : `background-color: ${COLORS.surface.hovered};`
        : ''}
				${props.buttonType === 'primary' &&
        props.size !== 'chip' &&
        !props.inverse &&
        !props.buttonColor
        ? `background-color: ${COLORS.background.inverse};`
        : ''}
			}
			${props.buttonType === 'text'
        ? getHoverButtonTextDecorationStyle(props.buttonType, props.disabled)
        : ''}
			${props.inverse
        ? generateInverseHoverBackground(props.buttonType, props.disabled)
        : ''}
		}`
    : `
			cursor: not-allowed;
			padding: 0;
		${getLoadingBorder(props.buttonType, props.size, props.inverse)}

		.button-container {
			display: flex;
			position: relative;
			width: 100%;
			.text {
				opacity: 0;
			}
			${props.buttonColor
        ? `background-color: ${getLoadingButtonBackgroundCustom(props.buttonColor, 15)};`
        : ''}
			padding: ${getPadding(props.buttonType, props.size)};
			.overlay-container {
				display: block;
				position: absolute;
				padding: 0;
				padding-top: 1px;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				${getLoadingBackground(props.buttonType, props.size, props.inverse, props.buttonColor)}
				.spinner {
					position: absolute;
					top: calc(50% - 14px);
					left: calc(50% - 7px);
				}

			}
		}`}

		.button-container {
		display: flex;
		gap: 8.33px;
		${(props) => (props.matchParentWidth ? 'margin: auto;' : '')}
	}
`;
export const ButtonCustomDashedBorder = () => {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "line-top" }), _jsx("div", { className: "line-left" }), _jsx("div", { className: "line-bottom" }), _jsx("div", { className: "line-right" })] }));
};
//# sourceMappingURL=Button.styled.js.map